'use client';

import * as React from 'react';

// Module-level persistence of the last focused item id, namespaced per consumer
// `scope` so multiple lists sharing the same restore key (e.g. the conversation
// list and the message list are both keyed by the chat store) cannot overwrite
// each other's stored focus.
const lastFocusedIdByKey = new WeakMap();
function readStoredId(restoreKey, scope) {
  return lastFocusedIdByKey.get(restoreKey)?.get(scope);
}
function writeStoredId(restoreKey, scope, id) {
  let scopes = lastFocusedIdByKey.get(restoreKey);
  if (scopes == null) {
    scopes = new Map();
    lastFocusedIdByKey.set(restoreKey, scopes);
  }
  scopes.set(scope, id);
}
export function useRovingFocus(params) {
  const {
    itemIds,
    restoreKey,
    scope,
    preferredId,
    fallback = 'first',
    onActivate,
    getTypeAheadLabel,
    enablePageKeys = true,
    pageSizeDivisor = 10,
    restoreFocusOnMount = false
  } = params;
  const storedFocusedId = readStoredId(restoreKey, scope);
  const restoreFocusIdRef = React.useRef(restoreFocusOnMount && storedFocusedId != null && itemIds.includes(storedFocusedId) ? storedFocusedId : null);
  // Only an *interactive* focus is stored as state; before the user interacts
  // the tab stop is derived per render so it tracks list changes (e.g. the
  // newest message) without state churn.
  const [focusedId, setFocusedIdState] = React.useState(() => storedFocusedId != null && itemIds.includes(storedFocusedId) ? storedFocusedId : undefined);
  const itemRefs = React.useRef(new Map());
  const pendingFocusIdRef = React.useRef(null);
  let effectiveFocusedId;
  if (focusedId != null && itemIds.includes(focusedId)) {
    effectiveFocusedId = focusedId;
  } else if (preferredId != null && itemIds.includes(preferredId)) {
    effectiveFocusedId = preferredId;
  } else {
    effectiveFocusedId = fallback === 'last' ? itemIds[itemIds.length - 1] : itemIds[0];
  }
  const focusItem = React.useCallback(id => {
    if (id == null) {
      return;
    }
    const element = itemRefs.current.get(id);
    if (element != null) {
      pendingFocusIdRef.current = null;
      element.focus();
    } else {
      // Windowing seam: the target row isn't mounted — retry on registration.
      pendingFocusIdRef.current = id;
    }
  }, []);
  const registerItemRef = React.useCallback((id, element) => {
    if (element == null) {
      itemRefs.current.delete(id);
      return;
    }
    itemRefs.current.set(id, element);
    if (pendingFocusIdRef.current === id) {
      pendingFocusIdRef.current = null;
      element.focus();
    }
  }, []);
  const setFocusedId = React.useCallback(id => {
    setFocusedIdState(id);
    writeStoredId(restoreKey, scope, id);
  }, [restoreKey, scope]);
  React.useEffect(() => {
    if (restoreFocusIdRef.current == null) {
      return;
    }
    focusItem(restoreFocusIdRef.current);
    restoreFocusIdRef.current = null;
  }, [focusItem]);
  const moveFocus = React.useCallback(targetIndex => {
    const boundedIndex = Math.max(0, Math.min(targetIndex, itemIds.length - 1));
    const targetId = itemIds[boundedIndex];
    if (targetId == null) {
      return;
    }
    setFocusedId(targetId);
    focusItem(targetId);
  }, [focusItem, itemIds, setFocusedId]);
  const typeAheadRef = React.useRef({
    buffer: '',
    resetTimer: null
  });
  const moveFocusToLabelPrefix = React.useCallback((prefix, fromIndex) => {
    if (prefix === '' || getTypeAheadLabel == null) {
      return;
    }
    const lowercase = prefix.toLowerCase();
    const total = itemIds.length;
    for (let offset = 1; offset <= total; offset += 1) {
      const candidateId = itemIds[(fromIndex + offset) % total];
      const label = (candidateId == null ? undefined : getTypeAheadLabel(candidateId))?.toLowerCase();
      if (label != null && label.startsWith(lowercase)) {
        setFocusedId(candidateId);
        focusItem(candidateId);
        return;
      }
    }
  }, [focusItem, getTypeAheadLabel, itemIds, setFocusedId]);
  const handleKeyDown = React.useCallback((event, id) => {
    const currentIndex = itemIds.indexOf(id);
    if (currentIndex === -1) {
      return;
    }

    // Page-size moves a percentage of the list at a time, with a sane
    // minimum so short lists still move by at least one item.
    const pageSize = Math.max(1, Math.floor(itemIds.length / pageSizeDivisor));
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(currentIndex + 1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(currentIndex - 1);
        return;
      case 'Home':
        event.preventDefault();
        moveFocus(0);
        return;
      case 'End':
        event.preventDefault();
        moveFocus(itemIds.length - 1);
        return;
      case 'PageDown':
        if (enablePageKeys) {
          event.preventDefault();
          moveFocus(currentIndex + pageSize);
        }
        return;
      case 'PageUp':
        if (enablePageKeys) {
          event.preventDefault();
          moveFocus(currentIndex - pageSize);
        }
        return;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        if (onActivate != null) {
          event.preventDefault();
          onActivate(id);
        }
        return;
      default:
        break;
    }
    if (getTypeAheadLabel == null) {
      return;
    }

    // Type-ahead: a single printable character (no modifier) appends to a
    // small buffer and jumps focus to the first item whose label starts
    // with that buffer.
    const isPrintable = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
    if (!isPrintable) {
      return;
    }
    event.preventDefault();
    typeAheadRef.current.buffer += event.key;
    if (typeAheadRef.current.resetTimer != null) {
      clearTimeout(typeAheadRef.current.resetTimer);
    }
    typeAheadRef.current.resetTimer = setTimeout(() => {
      typeAheadRef.current.buffer = '';
      typeAheadRef.current.resetTimer = null;
    }, 800);
    moveFocusToLabelPrefix(typeAheadRef.current.buffer, currentIndex);
  }, [enablePageKeys, getTypeAheadLabel, itemIds, moveFocus, moveFocusToLabelPrefix, onActivate, pageSizeDivisor]);
  React.useEffect(() => () => {
    if (typeAheadRef.current.resetTimer != null) {
      clearTimeout(typeAheadRef.current.resetTimer);
    }
  }, []);
  return {
    effectiveFocusedId,
    registerItemRef,
    setFocusedId,
    handleKeyDown,
    moveFocus,
    focusItem
  };
}